import { useState } from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PassAlgarveLogo } from "@/components/PassAlgarveLogo";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import {
  Facebook, Instagram, Youtube, MessageCircle, Mail, MapPin,
} from "lucide-react";

/**
 * Site-wide footer used on the Landing Page and all internal pages.
 *
 * Quick-link items use react-router <Link> to "/#hash" so they navigate
 * back to the Landing Page sections regardless of the current route.
 */
export default function SiteFooter() {
  const { t, language, localizePath } = useLanguage();
  const [newsletterEmail, setNewsletterEmail] = useState("");

  const handleNewsletter = (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    toast.success(t("footer.subscribeSuccess"));
    setNewsletterEmail("");
  };

  const QUICK_LINKS = [
    [t("nav.home"), "/"],
    [t("nav.experiences"), "/#experiences"],
    [t("nav.passports"), "/#passport"],
    [t("nav.how"), "/como-funciona"],
    [t("nav.partners"), "/#partners"],
    [t("nav.about"), "/sobre"],
  ];

  const INFO_LINKS = language === "en"
    ? [["FAQ", "/#faq"], ["Terms & Conditions", "/terms"], ["Privacy Policy", "/privacy"], ["Cookie Policy", "/cookie-policy"]]
    : [["FAQ", "/#faq"], ["Termos e Condições", "/termos"], ["Política de Privacidade", "/privacidade"], ["Política de Cookies", "/cookies"]];

  return (
    <footer id="footer" className="bg-[#071A35] text-white pt-16 pb-8 border-t border-[#D4A85D]/15" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 mb-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-1">
            <PassAlgarveLogo size="small" />
            <p className="text-white/65 text-sm mt-4 leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="flex items-center gap-4 mt-5 text-[#D4A85D]">
              <a href="#" aria-label="Facebook" className="hover:text-white"><Facebook className="w-5 h-5" /></a>
              <a href="#" aria-label="Instagram" className="hover:text-white"><Instagram className="w-5 h-5" /></a>
              <a href="#" aria-label="YouTube" className="hover:text-white"><Youtube className="w-5 h-5" /></a>
              <a href="#" aria-label="WhatsApp" className="hover:text-white"><MessageCircle className="w-5 h-5" /></a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-4">{t("footer.quickLinks")}</h4>
            <ul className="space-y-2.5">
              {QUICK_LINKS.map(([lbl, to]) => (
                <li key={lbl}>
                  <Link to={localizePath(to)} className="text-white/65 hover:text-[#D4A85D] text-sm">{lbl}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Info Links */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-4">{t("footer.info")}</h4>
            <ul className="space-y-2.5">
              {INFO_LINKS.map(([l, href]) => (
                <li key={l}><Link to={localizePath(href)} className="text-white/65 hover:text-[#D4A85D] text-sm">{l}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-4">{t("footer.contactUs")}</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2 text-white/65 text-sm">
                <Mail className="w-4 h-4 text-[#D4A85D]" />
                <a href="mailto:info@passalgarve.com" className="hover:text-[#D4A85D]">info@passalgarve.com</a>
              </li>
              <li className="flex items-start gap-2 text-white/65 text-sm">
                <MapPin className="w-4 h-4 text-[#D4A85D] mt-0.5" />
                <span>Lagos, Algarve – Portugal</span>
              </li>
              <li className="flex items-start gap-2 text-white/65 text-sm">
                <MessageCircle className="w-4 h-4 text-[#D4A85D] mt-0.5" />
                <span>{t("footer.whatsappHours")}</span>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="text-white font-bold uppercase tracking-wider text-sm mb-4">{t("footer.newsletter")}</h4>
            <p className="text-white/65 text-sm mb-3">{t("footer.newsletterDesc")}</p>
            <form onSubmit={handleNewsletter} className="space-y-2">
              <Input
                type="email"
                placeholder={t("footer.emailPlaceholder")}
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                className="bg-white/8 border-[#D4A85D]/20 text-white text-sm placeholder:text-white/40 rounded-md"
                data-testid="newsletter-input"
              />
              <Button type="submit" className="w-full bg-[#D4A85D] hover:bg-[#c9994a] text-[#071A35] font-bold text-sm uppercase tracking-wider rounded-md" data-testid="newsletter-submit">
                {t("footer.subscribe")}
              </Button>
            </form>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/55 text-sm">© {new Date().getFullYear()} PASS Algarve. {t("footer.rights")}</p>
        </div>
        <p className="text-center text-white/35 text-[10px] uppercase tracking-widest mt-4" data-testid="footer-developer-credit">
          {t("footer.developedBy")} GORGEOUS BLOSSOM
        </p>
      </div>
    </footer>
  );
}
